import React, { useEffect, useRef, useState } from 'react'
import './create-edit-game-container.styles.scss';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Modal, { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { useDispatch, useSelector } from 'react-redux';
import CreateEditFormReview from './create-edit-form-review.component';
import { setManagingList } from '@/state/slices/contentSlice';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { RandomString } from '@/utils/randomString';
import { membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import CreateEditMechanics from './create-edit-mechanics.component';
import CreateEditName from './create-edit-name.component';
import ContentManager from '@/components/SHARED/content-manager/content-manager.component';
import CreateEditDesign from './create-edit-design.component';
import CreateEditRoster from './create-edit-roster.component';
import useAnalytics from '@/utils/useAnalytics';
import { getOptimizedImageNoCrop } from '@/utils/getOptimizedBlob';

const CreateEditGameContainer = () => {

    const profile = useSelector(state => state.userData.userProfile);
    const managingList = useSelector(state => state.contentData.managingList);
    const membership = useSelector(state => state.userData.membership);
    const root = useSelector(state => state.userData.root);
    const screenSize = useSelector(state => state.contentData.screenSize);
    const [formData, setFormData] = useState({});
    const [validCode, setValidCode] = useState(true);
    const [height, setHeight] = useState(`${window.height - 70}px`);
    const [confirmMessage, setConfirmMessage] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);
    const [title, setTitle] = useState('Create a New Game');
    const [loading, setLoading] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [joinCodeEdited, setJoinCodeEdited] = useState(false);
    const [tempIcon, setTempIcon] = useState({});
    const [tempHeader, setTempHeader] = useState({});
    const [contentManagerElement, setContentManagerElement] = useState(false);
    const { trackEvent, trackException } = useAnalytics();

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const dataRef = useRef({
        joinCode: null,
        access: [
            'points',
            'leaderboards',
            'levels',
            'badges',
            'currency',
            'store',
            'items',
        ]
    });
    const approveRef = useRef();
    const storage = getStorage();

    useEffect(() => {
        // const cardHeight = document.getElementById('new-game-form').clientHeight
        // setHeight(`${cardHeight + 117}px`);
        const scrollPoint = document.getElementById('scroll-point');
        if (scrollPoint) {
            scrollPoint.scrollIntoView({ 'behavior': 'smooth', 'top': '-20px' });
        }
        const parentContainer = document.getElementsByClassName('content-container')[0];
        if (parentContainer) {
            parentContainer.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
        switch (location.pathname) {
            case '/home/creategame/name':
                setTitle('Give Your New Game a Name');
                break;
            case '/home/creategame/design':
                setTitle('Design the Game Elements');
                break;
            case '/home/creategame/mechanics':
                setTitle('Choose the Game Mechanics');
                break;
            case '/home/creategame/roster':
                setTitle('Choose How Players Join');
                break;
            case '/home/creategame/review':
                setTitle('Review Before Finalizing');
                break;
            default:
                setTitle('Create a New Game');
        }
    }, [location.pathname])

    useEffect(() => {
        if (!document.getElementById('new-game-form')) return;
        setTimeout(() => {
            // let card = document.getElementById('new-game-form');
            // if (!card) return;
            // const cardHeight = card.clientHeight
            // setHeight(`${cardHeight + 117}px`);
        }, 50);
    }, [formData])

    useEffect(() => {
        checkMembership();
    }, [])

    function checkMembership() {
        const res = testMemberRestrictions({ 'gameCount': Object.values(managingList).length, 'membership': membership });
        if (res.disabled && res.disabledReason.includes('games')) {
            const planRest = membershipRestrictions[membership.membership];
            const message = `While using the ${planRest.title}, you are only able to have ${planRest.gameCount} active Games.`;
            setShowUpgradeModal(message);
            return;
        }
    }
    // useEffect(() => {
    //     if (!tempGame) { return; }
    //     dataRef.current = tempGame;
    //         setFormData(dataRef.current);
    // }, [tempGame])

    function saveData(key, value) {
        if (key === 'enroll_limitList' && !value) {
            let tempObj = { ...dataRef.current };
            delete tempObj[key];
            dataRef.current = tempObj;
            setFormData(dataRef.current);
            return;
        }
        if (key === 'gameName' && !joinCodeEdited) {
            const convertedCode = value.toLowerCase().replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-')
            saveData('joinCode', convertedCode);
        }
        dataRef.current = { ...dataRef.current, ...{ [key]: value } }
        setFormData(dataRef.current);
        // window.localStorage.setItem('tempData', JSON.stringify(dataRef.current));
    }

    function cancel() {
        setConfirmMessage('Are you sure you want to stop creating this game? All of your progress so far will be lost.');
        approveRef.current = () => {
            approveCancel()
        };

        function approveCancel() {
            dataRef.current = {};
            setFormData(dataRef.current);
            // window.localStorage.removeItem('tempData');
            // dispatch(setTempGame(null));
            navigate('/home/managing');
            approveRef.current = null;
        }
    }

    async function verifyAndMoveOn(req, path) {
        setLoading(true);
        const incomplete = req.filter(r => !formData[r]).length > 0
        if (incomplete) {
            setAlertMessage('Please complete the required parts of the form before moving on.')
            setLoading(false);
            return;
        }
        else {
            if (path === 'part2') {
                const joinCodeIsAvailable = await UseCloudFunction('checkJoinCode', { 'joinCode': formData.joinCode, 'appId': profile.appId })
                setLoading(false);
                if (!joinCodeIsAvailable.result) {
                    setValidCode(false)
                    return;
                }
            }

            if (path === 'final') {
                setConfirmMessage(`Are you sure you're ready to save this game?`);
                approveRef.current = () => approveSave();

                async function approveSave() {
                    setLoading(true);
                    const now = new Date().getTime(); 
                    let newObj = {
                        ...formData, ...{
                            'ts': now,
                            'gameId': `${now}-${profile.appId.substring(0, 7)}-game`,
                            'creatorId': profile.appId,
                            'creatorName': `${profile.fName} ${profile.lName}`,
                            'ts_created': now,
                            'status': 'active'
                        }
                    };
                    if (newObj.icon.upload) {
                        const newUrl = await uploadImage(
                            newObj.icon.file,
                            newObj.gameId,
                            'game-icon',
                            newObj.icon.name,
                            'Game Icons'
                        );
                        newObj.icon = newUrl;
                    } else {
                        newObj.icon = newObj.icon.url;
                    }
                    if (newObj.header.upload) {
                        const newUrl = await uploadImage(
                            newObj.header.file,
                            newObj.gameId,
                            'game-header',
                            newObj.header.name,
                            'Game Headers'
                        );
                        newObj.header = newUrl;
                    } else {
                        newObj.header = newObj.header.url;
                    }
                    const res = await UseCloudFunction(
                        'saveGame',
                        {
                            'gameObj': newObj,
                            'profile': profile,
                            'action': 'new game'
                        }
                    )
                    if (res.error) {
                        setAlertMessage('Something went wrong. Please try again later. Error: ' + res.error);
                        setLoading(false);
                        trackException(`Game Creation Error: ${res.error}`);
                        return;
                    }
                    dispatch(setManagingList({ ...managingList, ...{ [newObj.gameId]: newObj } }))
                    setLoading(false);
                    window.localStorage.removeItem('tempData');
                    trackEvent(
                        'game_created',
                        'Game',
                        `User ${profile.appId} created a new game: ${newObj.gameId}`,
                        null,
                        membership
                    );
                    navigate(`/manage/${res.path}`)
                    // dataRef.current = {};
                    // setFormData(dataRef.current);
                }
            } else {
                setLoading(false);
                navigate(`/home/creategame/${path}`)
            }
        }
    }

    async function uploadImage(blob, gameId, type, name, elementType) {
        const now = new Date().getTime();
        const path = `${profile.appId}/${now}-${type}`;
        const profileRef = ref(storage, path)
        const metadata = {
            customMetadata: {
                'appId': profile.appId,
                'gameId': gameId,
            }
        };
        const optimizedBlob = await getOptimizedImageNoCrop(blob, 25);
        return uploadBytes(profileRef, optimizedBlob, metadata).then(async (snapshot) => {
            const newPath = await getDownloadURL(profileRef)
                .then((url) => {
                    let cropped = true;
                    if (elementType === 'Game Headers') {
                        cropped = false;
                    }
                    UseCloudFunction(
                        'saveToMyContent',
                        {
                            'appId': profile.appId,
                            'source': elementType,
                            'category': 'Images',
                            'cropped': cropped,
                            'contentData': {
                                'name': name,
                                'url': `storage:${path}`,
                                'type': 'image',
                                'id': `${now}_${RandomString(10)}_image`
                            },
                        }
                    )
                    return `storage:${path}`;
                })
            return newPath;
        })
    }

    function chooseElement(element) {
        let type = contentManagerElement.toLowerCase().replace('game', '')
        if (element.type === 'image') {
            document.getElementById(`selected-${type}`).classList.remove('hide');
            let obj = {
                'url': element.url.includes('https://') ? element.url : `${root}${element.url}`,
                'upload': false
            };
            if (type === 'icon') {
                setTempIcon(obj);
            } else if (type === 'header') {
                setTempHeader(obj);
            }
            setContentManagerElement(null);
        }
    }

    return (
        <div className={`create-edit-game-container ${screenSize}`}>
            {/* <Layout> */}
            <div className={`create-game-content ${screenSize}`} style={{ 'height': height }} >
                <div className='g-card section-title'>
                    {title}
                </div>
                <div className="g-space-1"></div>
                {/* <hr className='no-top-margin' /> */}
                <div>
                    <Routes>
                        <Route path='' element={
                            membership && membership.membership === 0 ?
                                <div className='g-card'>
                                    <CreateEditMechanics
                                        formData={formData}
                                        saveData={saveData}
                                        verifyAndMoveOn={verifyAndMoveOn}
                                        cancel={cancel} />
                                </div>
                                :
                                <Navigate to='/home/creategame/name' replace={true} />
                        }></Route>
                        <Route path='name' element={
                            <div className='g-card'>
                                <CreateEditName
                                    formData={formData}
                                    saveData={saveData}
                                    verifyAndMoveOn={verifyAndMoveOn}
                                    cancel={cancel}
                                    validJoinCode={validCode}
                                    loading={loading}
                                    setJoinCodeEdited={setJoinCodeEdited} />
                            </div>
                        }></Route>
                        <Route path='design' element={
                            <div className='g-card'>
                                <CreateEditDesign
                                    formData={formData}
                                    saveData={saveData}
                                    verifyAndMoveOn={verifyAndMoveOn}
                                    cancel={cancel}
                                    setContentManagerElement={setContentManagerElement}
                                    tempIcon={tempIcon}
                                    setTempIcon={setTempIcon}
                                    tempHeader={tempHeader}
                                    setTempHeader={setTempHeader} />
                            </div>
                        }></Route>
                        <Route path='roster' element={
                            <div className='g-card'>
                                <CreateEditRoster
                                    formData={formData}
                                    saveData={saveData}
                                    verifyAndMoveOn={verifyAndMoveOn}
                                    cancel={cancel} />
                            </div>
                        }></Route>
                        <Route path='review' element={
                            <div className='g-card'>
                                <CreateEditFormReview
                                    formData={formData}
                                    saveData={saveData}
                                    verifyAndMoveOn={verifyAndMoveOn}
                                    cancel={cancel}
                                    loading={loading} />
                            </div>
                        }></Route>
                    </Routes>
                    <div className="g-space-1-5"></div>
                </div>
            </div>
            <div className='g-space-20'></div>
            {/* </Layout> */}
            <ModalConfirm
                show={confirmMessage}
                cancel={() => setConfirmMessage(null)}
                message={confirmMessage}
                onApprove={approveRef.current} />
            <ModalAlert
                show={alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage} />
            <UpgradeRequiredModal
                show={showUpgradeModal}
                cancel={() => setShowUpgradeModal(false)}
                closeButton={false}
                message={showUpgradeModal}
            />
            <Modal
                show={contentManagerElement}
                cancel={() => setContentManagerElement(false)}
                closeButton={true}
                text="Content Manager"
                cls="wide" >
                <ContentManager
                    element={contentManagerElement}
                    chooseElement={chooseElement} />
            </Modal>
        </div>
    )
}

export default CreateEditGameContainer